import {ViolationSearchResponse} from '@entities/violations/types';
import api from '@shared/api';

import {
    DictionarySearchResponse,
    NotificationContentDto,
    NotificationContentSearchResponse,
    NotificationDetailsResponse,
    NotificationDto,
    NotificationMessageDto,
    NotificationSearchPayload,
    NotificationSearchResponse,
} from './types';

const NOTIFICATIONS_PATH = 'dda-notifications/api/v1';
const NSI_PATH = 'dda-trs-nsi-api/api/v1';
const VIOLATIONS_PATH = 'dda/api/v1';

const downloadFile = (filename: string, data: BlobPart, contentType: string) => {
    const blob = new Blob([data], {type: contentType});
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
};

const enrichWithViolationCreated = async (
    items: NotificationDto[],
): Promise<NotificationSearchResponse> => {
    if (!items.length) return {data: items, total: 0};

    const violationIds = Array.from(
        new Set(
            items.map((item) => item.violationId).filter((item): item is string => Boolean(item)),
        ),
    );

    if (!violationIds.length) {
        return {data: items, total: items.length};
    }

    const {data: violations} = await api.post<ViolationSearchResponse>(
        `${VIOLATIONS_PATH}/violation/search`,
        {
            filter: {
                type: 'AND',
                children: [
                    {type: 'IN', field: 'id', values: violationIds},
                    {type: 'ISNULL', field: 'validTo', values: []},
                ],
            },
            sorts: null,
            page: {page: 0, size: violationIds.length},
        },
    );

    const createdMap = new Map(
        (violations?.data ?? []).map((item) => [item.id, item.created ?? null] as const),
    );

    return {
        data: items.map((item) => ({
            ...item,
            violationCreated: (item.violationId && createdMap.get(item.violationId)) ?? null,
        })),
        total: items.length,
    };
};

export const fetchNotifications = async (
    payload: NotificationSearchPayload,
): Promise<NotificationSearchResponse> => {
    const {data} = await api.post<NotificationSearchResponse>(
        `${NOTIFICATIONS_PATH}/notification/search`,
        payload,
    );

    const enriched = await enrichWithViolationCreated(data.data ?? []);
    return {
        data: enriched.data,
        total: data.total ?? 0,
    };
};

export const fetchNotificationKinds = async () =>
    (
        await api.post<DictionarySearchResponse>(
            `${NOTIFICATIONS_PATH}/dict_notification_kind/search`,
            {
                page: {page: 0, size: 10000},
            },
        )
    ).data;

export const fetchNotificationStatuses = async () =>
    (
        await api.post<DictionarySearchResponse>(`${NSI_PATH}/dict_notification_status/search`, {
            page: {page: 0, size: 10000},
        })
    ).data;

export const fetchViolationTypes = async () =>
    (
        await api.post<DictionarySearchResponse>(`${NSI_PATH}/dict_violation_type/search`, {
            page: {page: 0, size: 10000},
        })
    ).data;

export const fetchNotificationMessage = async (notificationId: string) =>
    (
        await api.get<NotificationMessageDto>(
            `${NOTIFICATIONS_PATH}/notification/${notificationId}/defaultContent`,
        )
    ).data;

export const fetchNotificationContents = async (messageId: string) =>
    (
        await api.post<NotificationContentSearchResponse>(`${NOTIFICATIONS_PATH}/content/search`, {
            filter: {type: 'EQ', field: 'message.id', values: [messageId]},
            sorts: null,
            page: {page: 0, size: 50},
        })
    ).data;

export const fetchNotificationDetails = async (
    notificationId: string,
): Promise<NotificationDetailsResponse> => {
    const message = await fetchNotificationMessage(notificationId);
    const contents = await fetchNotificationContents(message.id);

    return {
        message,
        contents: contents.data ?? [],
    };
};

export const downloadNotificationMessage = async (notificationId: string) => {
    const message = await fetchNotificationMessage(notificationId);
    const contentType = message.bodyContentType?.mediaType || 'application/octet-stream';
    const code = message.bodyContentType?.code;

    if (code === 'PDF') {
        const {data} = await api.get<ArrayBuffer>(
            `${NOTIFICATIONS_PATH}/message/${message.id}/bodyBin`,
            {responseType: 'arraybuffer'},
        );
        downloadFile('notification.pdf', data, contentType);
        return;
    }

    const extension = code === 'HTML' ? 'html' : 'txt';
    downloadFile(`notification.${extension}`, message.body ?? '', contentType);
};

export const downloadNotificationContent = async (contentId: string) => {
    const {data: content} = await api.get<NotificationContentDto>(
        `${NOTIFICATIONS_PATH}/content/${contentId}`,
    );
    const contentType = content.type?.mediaType || 'application/octet-stream';

    if (content.type?.code === 'PDF') {
        const {data} = await api.get<ArrayBuffer>(
            `${NOTIFICATIONS_PATH}/content/${contentId}/bodyBin`,
            {
                responseType: 'arraybuffer',
            },
        );
        downloadFile(content.name || `attachment-${contentId}.pdf`, data, contentType);
        return;
    }

    downloadFile(
        content.name || `attachment-${contentId}.${content.type?.code === 'HTML' ? 'html' : 'txt'}`,
        content.body ?? '',
        contentType,
    );
};
